import { useState } from 'react'
import React from 'react'
import { useNavigate } from 'react-router-dom'

import { ResourceHeader } from '@factorialco/factorial-one-react/dist/experimental'
import { Add, InfoCircleLine, Reset } from '@factorialco/factorial-one-react/icons/app'
import { Features } from 'generated/featureDefinitions'
import { ReportsDashboard, ReportsReport } from 'generated/resources/graphql'
import { compact, isUndefined } from 'lodash'

import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import { useAsyncCan } from 'modules/core/lib/useAsyncCan'
import app from 'modules/core/store/models/app'

import employees from 'modules/employees/store/collections/employees'
import { useReportTracking } from 'modules/reports/hooks/useReportTracking'
import { useUserETL } from 'modules/reports/hooks/useUserETL'
import buildVariablesForms from 'modules/reports/lib/buildVariablesForms'
import { Variable, variableValueToBackend } from 'modules/reports/store/models/Report'

import Sidepanel from './Sidepanel'

export const ADD_FILTER_ACTION = 'addFilter'

interface Author {
  author?: {
    employee?: {
      id: number
    } | null
  } | null
}

interface Props {
  dashboard: Pick<ReportsDashboard, 'id' | 'title' | 'description' | 'variables'> & Author
  reports: Pick<ReportsReport, 'id'>[]
}

const DashboardHeader = ({ dashboard, reports }: Props) => {
  const dashboard_id = dashboard.id
  const [isCreateReportModalOpen, setCreateReportModalOpen] = useState(false)
  const { track } = useReportTracking()
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const author = employees.get(dashboard.author?.employee?.id)
  const { value: canManage } = useAsyncCan('reports.manage', author)

  const { featureAvailability } = useFeatureAvailability(Features.analytics_advanced_reports)
  if (!dashboard_id) throw 'Dashboard doesnt exist'

  const {
    loading: loadingUserETL,
    isRunning,
    lastRefreshTime,
    forceRefresh,
  } = useUserETL({
    readOnly: false,
  })

  const refreshNowButton = i18n.t('reports.refresh_now')
  const refreshingMessage = i18n.t('reports.refreshing_message')

  const refreshMessage = isRunning ? refreshingMessage : refreshNowButton

  const isEmpty = reports.length < 1
  const isRunningFirstTime = isUndefined(lastRefreshTime) && isRunning

  const dashboardFiltersValues: Variable[] = compact(
    (dashboard.variables || []).map(({ type, defaultValue, name }) => {
      if (!type) return undefined

      return {
        type: type.toString(),
        value: undefined,
        default_value: defaultValue?.toString(),
        name,
      }
    })
  )

  const filtersValues = buildVariablesForms(dashboardFiltersValues)

  const download = () => {
    app.setFlash({
      type: 'correct',
      message: i18n.t('custom_reports.header.download_excel_email'),
    })

    const filters = new URLSearchParams()
    filtersValues.forEach((filter) => {
      const { name, value, default_value, type } = filter.data()
      const actual = value && value !== '' ? value : default_value
      const transformedValue = variableValueToBackend(type, actual)

      filters.append(name, transformedValue)
    })

    track(trackingEvents.reports.downloadReportSuccess)
    const baseUrl = buildUrl(`/reports/dashboards/${dashboard.id}/export`)
    window.location.href = `${baseUrl}?${filters.toString()}`
  }

  return (
    <>
      <Sidepanel
        open={isCreateReportModalOpen}
        onClose={() => setCreateReportModalOpen(false)}
        // eslint-disable-next-line no-type-assertion/no-type-assertion
        dashboardId={dashboard.id!}
      />
      <ResourceHeader
        title={dashboard.title}
        description={dashboard.description || ''}
        primaryAction={{
          label: i18n.t('custom_reports.dashboard.landing_new_from_gallery'),
          onClick: () => setCreateReportModalOpen(true),
          icon: Add,
          isVisible: canManage && !isRunningFirstTime,
        }}
        secondaryActions={[
          {
            label: i18n.t('reports.know_more_text'),
            icon: InfoCircleLine,
            isVisible: !isRunningFirstTime,
            onClick: () => {
              window.location.href = i18n.t('reports.know_more_link')
            },
          },
          {
            isVisible: !loadingUserETL && !isEmpty && !isRunningFirstTime,
            disabled: isRunning,
            icon: Reset,
            label: refreshMessage,
            onClick: () => {
              forceRefresh()
            },
          },
        ]}
        otherActions={[
          {
            label: i18n.t('custom_reports.dashboards.actions.change_name'),
            isVisible: canManage && !isRunningFirstTime,
            onClick: () => {
              navigate({
                pathname: navigation.reports.dashboards.dashboardId.list.edit.fullPath({
                  dashboard_id,
                }),
              })
            },
          },
          {
            label: i18n.t('custom_reports.add_filters'),
            isVisible: featureAvailability.available && !isEmpty && !isRunningFirstTime,
            onClick: () => {
              navigate({
                pathname: navigation.reports.dashboards.dashboardId.list.fullPath({
                  dashboard_id,
                }),
                search: `?action=${ADD_FILTER_ACTION}`,
              })
            },
          },
          {
            label: i18n.t('common.download_all'),
            isVisible: !isEmpty && !isRunningFirstTime,
            onClick: () => {
              download()
            },
          },
          {
            label: i18n.t('custom_reports.dashboards.actions.delete'),
            isVisible: canManage && !isRunningFirstTime,
            onClick: () => {
              navigate({
                pathname: navigation.reports.dashboards.dashboardId.list.delete.fullPath({
                  dashboard_id,
                }),
              })
            },
          },
        ]}
        metadata={[
          lastRefreshTime && {
            label: i18n.t('reports.last_updated_message'),
            value: { type: 'text', content: lastRefreshTime.fromNow() },
          },
        ]}
      />
    </>
  )
}

export default DashboardHeader
